/**
 * Member invocation-page route (OL-258) — eagerly registers
 * `/orca/loops/$loopId/invocations/$invocationId`, validates `?tab=`, and runs
 * the SSR seeder; the page itself (shell, graph, timeline, budgets, compare)
 * lives in `$invocationId.lazy.tsx` so none of the loop-viewer component set
 * lands in the always-loaded chunk (`packages/web/budgets.json`, "Web Bundle
 * Budgets").
 *
 * `orcaloops.ai/loops/<id>/invocations/<id>` rewrites onto this path — every
 * orcaloops.ai path does, because `/orca` is a WILDCARD domain prefix — which is
 * why the file lives under `routes/orca/` and not a bare `routes/loops/` the
 * rewrite could never reach.
 *
 * **`requireAuth`, never `requireAdmin`** — the canvas route's reasoning
 * exactly. `/orca/loops` is an `ORCA_PUBLIC_ROOTS` entry so the `/orca` layout
 * guard steps aside on the ADMIN check; this guard puts a session back in its
 * place. A person watching their own loop run is not an operator, and the admin
 * guard would bounce the page's only audience to `/access-denied`. The session
 * is necessary but not sufficient: `LoopViewerRpcs` resolves the owning customer
 * from the actor and answers `Forbidden` for everybody else.
 *
 * The guard runs BEFORE the seeder and the seeder's result is merged into the
 * route context, so a signed-out visitor is redirected without the server ever
 * reading the invocation, and a signed-in one gets the walk painted on first
 * byte instead of a spinner.
 *
 * @since 1.0.0
 */
import { createFileRoute } from "@tanstack/react-router";

import { loopViewerStylesheetLinks } from "~/lib/loop-shell-head.ts";
import type { BeforeLoadArgs } from "~/lib/orca-ssr.ts";
import { loopInvocationBeforeLoad } from "~/lib/orca-ssr.ts";
import { requireAuth } from "~/lib/server-auth.ts";

/**
 * The five panels the invocation page offers, in the order the tab strip shows
 * them.
 *
 * @since 1.0.0
 */
export const LOOP_INVOCATION_TABS = ["graph", "timeline", "budgets", "compare", "rows"] as const;

/**
 * One of {@link LOOP_INVOCATION_TABS}.
 *
 * @since 1.0.0
 */
export type LoopInvocationTab = (typeof LOOP_INVOCATION_TABS)[number];

/**
 * The invocation page's search contract: an optional `?tab=` naming the open
 * panel. Absent means the page's own default (the graph).
 *
 * @since 1.0.0
 */
export interface LoopInvocationSearch {
  readonly tab?: LoopInvocationTab;
}

/**
 * Keep `tab` only when it names a real panel; anything else is dropped so a
 * stale or hand-typed `?tab` reads as "the default panel" rather than erroring
 * the whole route out. Mirrors `validateLoopCanvasSearch`'s drop-don't-throw
 * contract.
 *
 * @since 1.0.0
 */
export const validateLoopInvocationSearch = (search: Record<string, unknown>): LoopInvocationSearch => {
  const raw = search["tab"];
  const match = LOOP_INVOCATION_TABS.find((tab) => tab === raw);
  return match ? { tab: match } : {};
};

/**
 * The viewer's stylesheets as render-blocking head links — the canvas route's
 * `loopCanvasStylesheetLinks`, for the same reason: the seeder makes the server
 * render the whole viewer, and a sheet the SSR markup depends on has to be a
 * `<link>` in the head the server sends, not the lazy chunk's side-effect import
 * (`packages/web/CLAUDE.md`, "Performance budgets", rule 6).
 *
 * The set is the orcaloops frame sheet plus the graph's. Every one arrives through a `?url` import,
 * which keeps this file eager-safe.
 *
 * @since 1.0.0
 */
export const loopInvocationStylesheetLinks = loopViewerStylesheetLinks;

/**
 * The route's `head()`, extracted so it is unit-testable without the route
 * machinery.
 *
 * @since 1.0.0
 */
export const loopInvocationHead = () => ({ links: loopInvocationStylesheetLinks });

/**
 * The guard + seeder, extracted so it is unit-testable without the route
 * machinery.
 *
 * @since 1.0.0
 */
// oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack redirects require async/await
export const loopInvocationPageBeforeLoad = async (args: BeforeLoadArgs) => {
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack redirects require async/await
  const auth = await requireAuth();
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- the SSR seeder returns a Promise
  const seeded = await loopInvocationBeforeLoad(args);
  return { auth, ...seeded };
};

export const Route = createFileRoute("/orca/loops/$loopId/invocations/$invocationId")({
  validateSearch: validateLoopInvocationSearch,
  beforeLoad: loopInvocationPageBeforeLoad,
  head: loopInvocationHead,
});
